import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";

import { InstitutionWithAddresses } from "../../types/institution.types";
import { useInstitutions } from "../../context/InstitutionsContext";
import { useTableControls } from "../../hooks/useTableControls";
import { formatPhoneNumber } from "../../utils/format-phone-number.util";

import CrudTable from "../../components/crud-table";
import { CrudTableHeader } from "../../components/crud-table-header";
import { ErrorMessage } from "../../components/error-message";
import { Loading } from "../../components/loading";

export interface IInstitutionsProps {}

export default function Institutions(props: IInstitutionsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isListView = location.pathname === "/dashboard/institutions";

  const {
    institutions,
    getInstitutions,
    error,
    isLoading,
    handleDeleteInstitution,
  } = useInstitutions();

  const {
    paginatedData: paginatedInstitutions,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setCurrentPage,
  } = useTableControls(institutions || []);

  useEffect(() => {
    getInstitutions();
  }, [getInstitutions]);

  const handleAddInstitution = () => {
    navigate("new");
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof InstitutionWithAddresses | null,
      sortDirection,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      key: "institution.name" as keyof InstitutionWithAddresses,
      label: "Nombre",
      sortable: true,
    },
    {
      key: "institution.president" as keyof InstitutionWithAddresses,
      label: "Presidente",
      sortable: true,
    },
    {
      key: "institution.phone_number" as keyof InstitutionWithAddresses,
      label: "Teléfono",
      sortable: true,
      render(value: string) {
        return formatPhoneNumber(value);
      },
    },
  ];

  return (
    <div className="w-full p-8">
      <div>
        <CrudTableHeader
          title="Barras pesqueras"
          count={institutions?.length}
          icon={<BuildingOffice2Icon />}
        />

        {error && (
          <div className="mb-4">
            <ErrorMessage message={error} dismissible />
          </div>
        )}

        {isLoading ? (
          <Loading />
        ) : (
          isListView && (
            <CrudTable
              data={paginatedInstitutions}
              columns={columns}
              onAdd={{
                label: "Agregar barra pesquera",
                onClick: handleAddInstitution,
              }}
              actions={{
                edit: {
                  label: "",
                  onClick: (row) => {
                    navigate(`edit/${row.institution.id}`);
                  },
                },
                delete: {
                  label: "",
                  onClick: (institution) =>
                    handleDeleteInstitution(institution),
                },
              }}
              deleteConfirmation={{
                titleField: "institution.name",
                message: (row) =>
                  `¿Estás seguro de que deseas eliminar la barra pesquera "${row.institution.name}"?`,
              }}
              search={{
                placeholder: "Buscar barra pesquera...",
                onSearch: handleSearch,
              }}
              sort={{
                sortBy: sortConfig.sortBy as string | null,
                sortDirection: sortConfig.sortDirection,
                onSort: handleSort,
              }}
              pagination={{
                page: currentPage,
                totalPages: totalPages,
                onPageChange: handlePageChange,
                labels: {
                  next: "Siguiente",
                  previous: "Anterior",
                  pageInfo: `Página ${currentPage} de ${totalPages}`,
                },
              }}
              totalCount={filteredData.length}
            />
          )
        )}
        <Outlet />
      </div>
    </div>
  );
}
